import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

export const exportRevenueReport = async (data, month, merchantInfo, descriptionLabel = "Doanh thu ngày") => {
  // merchantInfo: Object chứa { name, address, taxCode, ... }
  // data: Mảng doanh thu hàng ngày

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Sổ S1a-HKD");

  // 1. Cấu hình Font chữ mặc định (Times New Roman cho chuẩn văn bản)
  const fontStyle = { name: "Times New Roman", size: 11 };
  const borderStyle = {
    top: { style: "thin" },
    left: { style: "thin" },
    bottom: { style: "thin" },
    right: { style: "thin" },
  };

  // 2. Thiết lập độ rộng cột (Căn chỉnh theo ảnh: Cột B rộng nhất để Diễn giải)
  worksheet.columns = [
    { key: "date", width: 15 }, // Cột A: Ngày
    { key: "description", width: 50 }, // Cột B: Diễn giải (Rộng)
    { key: "amount", width: 20 }, // Cột C: Số tiền
  ];

  // --- PHẦN 1: THÔNG TIN CHỦ HỘ & MẪU SỐ (HEADER) ---

  // Dòng 1: Tên hộ kinh doanh
  worksheet.mergeCells("A1:B1");
  const cellA1 = worksheet.getCell("A1");
  cellA1.value = `HỘ, CÁ NHÂN KINH DOANH: ${merchantInfo?.name || "...................."}`;
  cellA1.font = { ...fontStyle, bold: true };

  // Khung Mẫu số bên phải (Gộp C1:C3)
  // Lưu ý: Trong ảnh, khung này nằm gọn ở bên phải.
  // Vì ExcelJS gộp cột, ta dùng cột C cho khung này.
  const cellC1 = worksheet.getCell("C1");
  cellC1.value =
    "Mẫu số S1a-HKD\n(Ban hành kèm theo Thông tư \nsố 152/2025/TT-BTC ngày \n31/12/2025 của Bộ Tài chính)";
  cellC1.font = { ...fontStyle, size: 10, italic: true };
  cellC1.alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: true,
  };
  cellC1.border = borderStyle;
  // Merge dọc xuống 3 dòng để ô to ra
  worksheet.mergeCells("C1:C3");

  // Dòng 2: Địa chỉ
  worksheet.mergeCells("A2:B2");
  const cellA2 = worksheet.getCell("A2");
  cellA2.value = `Địa chỉ: ${merchantInfo?.address || "...................."}`;
  cellA2.font = fontStyle;

  // Dòng 3: MST
  worksheet.mergeCells("A3:B3");
  const cellA3 = worksheet.getCell("A3");
  cellA3.value = `MST: ${merchantInfo?.taxCode || "...................."}`;
  cellA3.font = fontStyle;

  // --- PHẦN 2: TIÊU ĐỀ SỔ (TITLE) ---

  // Dòng 4: Tên sổ (In hoa, Đậm, Giữa)
  worksheet.mergeCells("A4:C4");
  const title = worksheet.getCell("A4");
  title.value = "SỔ DOANH THU BÁN HÀNG HÓA, DỊCH VỤ";
  title.font = { ...fontStyle, size: 14, bold: true };
  title.alignment = { vertical: "middle", horizontal: "center" };

  // Dòng 5: Địa điểm kinh doanh
  worksheet.mergeCells("A5:C5");
  const location = worksheet.getCell("A5");
  location.value = `Địa điểm kinh doanh: ${merchantInfo?.address || "...................."}`;
  location.font = { ...fontStyle, bold: true };
  location.alignment = { vertical: "middle", horizontal: "center" };

  // Dòng 6: Kỳ kê khai
  worksheet.mergeCells("A6:C6");
  const period = worksheet.getCell("A6");
  // Xử lý chuỗi tháng "2024-02" -> "Tháng 02 năm 2024"
  const [y, m] = (month || "2026-01").split("-");
  period.value = `Kỳ kê khai: Tháng ${m} năm ${y}`;
  period.font = { ...fontStyle, bold: true };
  period.alignment = { vertical: "middle", horizontal: "center" };

  // Dòng 7: Đơn vị tính (Căn phải)
  const unitRow = worksheet.getRow(7);
  const unitCell = unitRow.getCell(3); // Ô C7
  unitCell.value = "Đơn vị tính: đồng";
  unitCell.font = { ...fontStyle, italic: true };
  unitCell.alignment = { horizontal: "right" };

  // --- PHẦN 3: BẢNG DỮ LIỆU (TABLE HEADER) ---

  // Dòng 8: Header chính
  const headerRow = worksheet.getRow(8);
  headerRow.values = ["NGÀY, THÁNG GHI SỔ", "DIỄN GIẢI", "SỐ TIỀN"];
  headerRow.height = 30; // Cao hơn chút cho đẹp

  headerRow.eachCell((cell) => {
    cell.font = { ...fontStyle, bold: true };
    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true,
    };
    cell.border = borderStyle;
    // Màu nền xanh nhạt giống ảnh (E1EEF4)
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFE1EEF4" },
    };
  });

  // Dòng 9: Header phụ (A, B, 1)
  const subHeaderRow = worksheet.getRow(9);
  subHeaderRow.values = ["A", "B", "1"];
  subHeaderRow.eachCell((cell) => {
    cell.font = { ...fontStyle, bold: true };
    cell.alignment = { vertical: "middle", horizontal: "center" };
    cell.border = borderStyle;
    // Màu nền xám nhạt giống ảnh
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFEBEBEB" },
    };
  });

  // --- PHẦN 4: ĐỔ DỮ LIỆU (DATA LOOP) ---
  let totalRevenue = 0;

  // Sắp xếp dữ liệu theo ngày tăng dần
  const sortedData = [...data].sort(
    (a, b) => new Date(a.date) - new Date(b.date),
  );

  sortedData.forEach((item) => {
    totalRevenue += item.revenue;

    // Format ngày: YYYY-MM-DD -> DD/MM/YYYY
    const dateObj = new Date(item.date);
    const dateStr = `${String(dateObj.getDate()).padStart(2, "0")}/${String(dateObj.getMonth() + 1).padStart(2, "0")}/${dateObj.getFullYear()}`;

    const descriptionText = `${descriptionLabel} ${dateStr}`;

    const row = worksheet.addRow([
      dateStr,
      descriptionText, // Sử dụng nội dung custom
      item.revenue,
    ]);

    // const row = worksheet.addRow([
    //   dateStr,
    //   `Doanh thu ngày ${dateStr}`, // Tự động sinh diễn giải nếu data không có
    //   item.revenue,
    // ]);

    // Style cho từng ô dữ liệu
    row.getCell(1).alignment = { horizontal: "center" }; // Ngày căn giữa
    row.getCell(1).font = fontStyle;

    row.getCell(2).alignment = { horizontal: "left", wrapText: true }; // Diễn giải căn trái
    row.getCell(2).font = fontStyle;

    row.getCell(3).numFmt = "#,##0"; // Số tiền format phân cách ngàn
    row.getCell(3).font = fontStyle;

    // Kẻ khung
    row.eachCell((cell) => (cell.border = borderStyle));
  });

  // --- PHẦN 5: TỔNG CỘNG (FOOTER TABLE) ---
  const totalRow = worksheet.addRow(["Tổng cộng", "", totalRevenue]);

  // Merge ô Tổng cộng (A và B)
  worksheet.mergeCells(`A${totalRow.number}:B${totalRow.number}`);

  totalRow.getCell(1).alignment = { horizontal: "center" };
  totalRow.getCell(1).font = { ...fontStyle, bold: true };

  totalRow.getCell(3).numFmt = "#,##0";
  totalRow.getCell(3).font = { ...fontStyle, bold: true };

  // Kẻ khung cho dòng tổng
  totalRow.eachCell((cell) => (cell.border = borderStyle));

  // --- PHẦN 6: CHỮ KÝ (SIGNATURE) ---

  // Thêm dòng trống
  worksheet.addRow([]);

  // Dòng ngày tháng năm
  const signDateRow = worksheet.addRow(["", "", `Ngày ... tháng ... năm ${y}`]);
  signDateRow.getCell(3).font = { ...fontStyle, italic: true };
  signDateRow.getCell(3).alignment = { horizontal: "center" };

  // Dòng Người đại diện
  const signerTitleRow = worksheet.addRow([
    "",
    "",
    "Người đại diện hộ kinh doanh/cá nhân kinh doanh",
  ]);
  signerTitleRow.getCell(3).font = { ...fontStyle, bold: true };
  signerTitleRow.getCell(3).alignment = {
    horizontal: "center",
    wrapText: true,
  };

  // Dòng (Ký, ghi rõ...)
  const signNoteRow = worksheet.addRow([
    "",
    "",
    "(Ký, ghi rõ họ tên, đóng dấu (nếu có))",
  ]);
  signNoteRow.getCell(3).font = { ...fontStyle, italic: true };
  signNoteRow.getCell(3).alignment = { horizontal: "center" };

  // Tăng chiều cao dòng chữ ký cho thoáng
  worksheet.addRow([]);
  worksheet.addRow([]);
  worksheet.addRow([]);

  // Tên người ký (nếu có thông tin)
  const signerNameRow = worksheet.addRow(["", "", merchantInfo?.name || ""]);
  signerNameRow.getCell(3).font = { ...fontStyle, bold: true };
  signerNameRow.getCell(3).alignment = { horizontal: "center" };

  // --- XUẤT FILE ---
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, `SoDoanhThu_S1a_${month}.xlsx`);
};
